/**
 * Generates dummy data for form fields based on their type, validation rules, and country.
 * Used by DevStepPanel to auto-fill fields during development/testing.
 *
 * @param {Object} field - The field definition object
 * @param {Object} app - The current application data (reactive value)
 * @param {string} country - The project country code ('CA' or 'US')
 * @returns {*} A valid dummy value for the field
 */
export function generateDummyValue(field, app, country = 'CA') {
    const type = field.type;

    if (type === 'select' || type === 'radio') {
        const choices = resolveChoices(field, app);
        return choices.length > 0 ? choices[0].value : 'test';
    }

    if (type === 'checkbox') {
        if (field.isMultipleOption) {
            const choices = resolveChoices(field, app);
            return choices.length > 0 ? [choices[0].value] : true;
        }
        return true;
    }

    if (type === 'date') {
        return generateDateValue(field);
    }

    if (type === 'fulladdress') {
        return country === 'US' ? addressData.US : addressData.CA;
    }

    return generateValueFromRules(field, country);
}

const addressData = {
    CA: {
        street: '123 Main St',
        streetNumber: '123',
        postalCode: 'K1A 0B1',
        province: 'ON',
        city: 'Ottawa',
    },
    US: {
        street: '123 Main St',
        streetNumber: '123',
        postalCode: '90210',
        province: 'CA',
        city: 'Los Angeles',
    },
};

/**
 * @param {Object} field
 * @param {Object} app
 * @returns {Array}
 */
function resolveChoices(field, app) {
    if (typeof field.choices === 'function') {
        try {
            return field.choices({ ...app }) || [];
        } catch {
            return [];
        }
    }
    return Array.isArray(field.choices) ? field.choices : [];
}

/**
 * @param {Object} field
 * @returns {string}
 */
function generateDateValue(field) {
    const rules = field.validationRules || '';
    const today = new Date();

    if (rules.includes('dob')) {
        const dob = new Date(today.getFullYear() - 30, today.getMonth(), today.getDate());
        return formatDate(dob);
    }

    if ((field.name || '').toLowerCase().includes('paydate') || (field.name || '').toLowerCase().includes('pay_date')) {
        // Use +14 days instead of +1 month — avoids off-by-one month bug
        // (Date constructor expects 0-indexed months, getMonth()+1 skips a month)
        const paydate = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 14);
        return formatDate(paydate);
    }

    return formatDate(today);
}

/**
 * @param {Date} date
 * @returns {string}
 */
function formatDate(date) {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${yyyy}-${mm}-${dd}`;
}

const countryRuleValues = {
    CA: {
        phone: '6135551234',
        postal: 'K1A 0B1',
        city: 'Ottawa',
        province: 'ON',
        sin: '123456782',
        driversLicense: 'yes',
    },
    US: {
        phone: '2125551234',
        postal: '90210',
        city: 'Los Angeles',
        province: 'CA',
        ssn: '078051120',
        driversLicense: 'yes',
        driversLicenseUsFormat: 'A1234567',
        usGovernmentIdNumber: 'A1234567',
    },
};

/**
 * @param {Object} field
 * @param {string} country
 * @returns {string}
 */
function generateValueFromRules(field, country) {
    const rules = field.validationRules || '';
    const cv = countryRuleValues[country] || countryRuleValues.CA;

    for (const [rule, valueFn] of ruleValueMap) {
        if (rules.includes(rule)) {
            return typeof valueFn === 'function' ? valueFn(cv) : valueFn;
        }
    }

    return 'test';
}

/** @type {Array<[string, string | function]>} */
const ruleValueMap = [
    ['email', 'dev@test.com'],
    ['name', 'John'],
    ['canadianPhone', (cv) => countryRuleValues.CA.phone],
    ['usPhone', (cv) => countryRuleValues.US.phone],
    ['phone', (cv) => cv.phone],
    ['postal', (cv) => cv.postal],
    ['zip', (cv) => countryRuleValues.US.postal],
    ['password', 'DevPass1'],
    ['ssn', (cv) => cv.ssn || '078051120'],
    ['sin', (cv) => cv.sin || '123456782'],
    ['bankRoutingNumber', '021000021'],
    ['bankAccountNumber', '1234567890'],
    ['city', (cv) => cv.city],
    ['street', '123 Main St'],
    ['carYear', String(new Date().getFullYear())],
    ['driversLicenseUsFormat', (cv) => cv.driversLicenseUsFormat || 'A1234567'],
    ['usGovernmentIdNumber', (cv) => cv.usGovernmentIdNumber || 'A1234567'],
    ['driversLicense', 'yes'],
    ['canadianProvince', 'ON'],
    ['usState', 'CA'],
    ['numeric', '5000'],
    ['rate', '7'],
];
